/**
 * sellItemToTeam.js
 * 
 * Handles selling an item to a team:
 * - Adds the item to the team's items.
 * - Deducts the amount from the team's budgetLeft.
 * - Records the sale on the room (lastSale), so every screen can announce it.
 */

import { doc, arrayUnion, increment, writeBatch } from "firebase/firestore";
import { db } from "../../../config/firebase";
import { AuctionRoomsCollection, Teams } from "../../../assets/DatabaseCollections";

/**
 * Sells an item to a team.
 * 
 * @param {string} roomID - ID of the current auction room
 * @param {object} team - The team buying the item ({ id, name })
 * @param {object} item - The item ({ name, score, values })
 * @param {number} itemIndex - Position of the item in the room's library
 * @param {number} amount - The amount spent by the team
 * @param {object} roomUpdates - Extra fields to set on the room in the same write (next item, break)
 * @returns {boolean} success status
 */
export const sellItemToTeam = async (roomID, team, item, itemIndex, amount, roomUpdates = {}) => {
    try {
        const teamRef = doc(db, AuctionRoomsCollection, roomID, Teams, team.id);
        const roomRef = doc(db, AuctionRoomsCollection, roomID);
        const batch = writeBatch(db);

        // Teams from older rooms keep their items in 'movies'
        const itemsField = team.movies && !team.items ? "movies" : "items";

        batch.update(teamRef, {
            [itemsField]: arrayUnion({
                ...item,
                soldPrice: Number(amount),
                soldAt: new Date().toISOString()
            }),
            budgetLeft: increment(-Number(amount))
        });
        batch.update(roomRef, {
            moviesSold  :   increment(1),
            lastSale    :   {
                ...item,
                itemIndex   :   itemIndex,
                teamID      :   team.id,
                teamName    :   team.name,
                amount      :   Number(amount),
                soldAt      :   Date.now(),
            },
            ...roomUpdates,
        });

        await batch.commit();
        return true;
    } catch (error) {
        console.error("Error selling item:", error);
        return false;
    }
};
